import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { NARROW_QUERY, SCROLL_REGION_SX, SURFACE_SX } from './layoutStyles';
import { PageFrame, type PageFrameProps } from './PageFrame';
import { WS_LAYOUT } from './tokens';

export interface AdminLayoutProps extends Pick<PageFrameProps, 'header' | 'tabs' | 'fill'> {
  /** `<SideNav />`: the settings areas. */
  nav: ReactNode;
  /** `<FieldGroup>`s or a management table. Scrolls on its own. */
  children: ReactNode;
  /** `<SaveBar />` when the area edits settings; omit for a read-only or table area. */
  footer?: ReactNode;
}

/**
 * LAYOUT 10 — Administration (DESIGN.md): settings navigation beside grouped fields or a
 * management table, with a fixed save state. Users, SSO, audit, licensing and the rest share
 * this frame instead of each drawing its own.
 *
 * Narrow (899px or less): the navigation becomes a row above the content.
 */
export function AdminLayout({ header, tabs, nav, children, footer, fill }: AdminLayoutProps) {
  return (
    <PageFrame header={header} tabs={tabs} footer={footer} fill={fill}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'grid',
          gap: 4,
          gridTemplateColumns: `${WS_LAYOUT.adminNav}px minmax(0, 1fr)`,
          gridTemplateRows: 'minmax(0, 1fr)',
          [NARROW_QUERY]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: 'auto minmax(0, 1fr)',
          },
        }}
      >
        <Box sx={{ minWidth: 0, overflowY: 'auto' }}>{nav}</Box>
        <Box tabIndex={0} sx={{ ...SURFACE_SX, ...SCROLL_REGION_SX, p: 5 }}>
          {children}
        </Box>
      </Box>
    </PageFrame>
  );
}
